import { ResponsiveTable } from '@/components/shared/ResponsiveTable';
import { MovableDialog } from '@/components/shared/MovableDialog';
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactElement,
} from 'react';
import { v2, type Role, type RoleWriteBody } from '@/api/v2Client';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { Spinner } from '@/components/dashboard/Spinner';
import { PermissionDenied } from '@/pages/PermissionDenied';
import { ConfirmModal } from '@/components/shared/ConfirmModal';

import { formatApiError } from '@/lib/errors';
import {
  ACTION_DEFINITIONS,
  isKnownAction,
  permissionColumns,
} from '@/constants/permissionVocabulary';

/** Honest label + definition for any column, including one the vocabulary does not define yet. */
function describeAction(action: string): { label: string; definition: string; enforced: boolean } {
  if (isKnownAction(action)) return ACTION_DEFINITIONS[action];
  return { label: action, definition: 'No definition recorded for this action yet.', enforced: true };
}

const cellStyle: CSSProperties = {
  padding: '6px 12px',
  textAlign: 'center',
  fontSize: 13,
};

const badgeStyle: CSSProperties = {
  display: 'inline-block',
  marginLeft: 8,
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
  border: '1px solid var(--border)',
};

const buttonStyle: CSSProperties = {
  padding: '4px 10px',
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  cursor: 'pointer',
};

const createBtnStyle: CSSProperties = {
  ...buttonStyle,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  padding: '8px 16px',
  fontSize: 13,
};

const dangerBtnStyle: CSSProperties = {
  ...buttonStyle,
  background: 'var(--red-bg, #FEE2E2)',
  color: 'var(--red, #B91C1C)',
  border: '1px solid var(--red, #B91C1C)',
};

interface ModalOpenState {
  kind: 'create' | 'edit' | 'retire';
  role?: Role;
}

export function RolesPage(): ReactElement {
  const perms = useUserPermissions();
  const canRead = perms.hasPermission('Users', 'read');
  const canManage = perms.hasPermission('Users', 'manage');
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState<ModalOpenState | null>(null);
  // phase-fix-role-retire-not-delete — default ON so retired roles never
  // silently vanish from the list; component state only, not persisted.
  const [showRetired, setShowRetired] = useState(true);
  // T-07 (D-28) — the actions of the permission catalogue. GET /permissions
  // checks Users.manage, so a Users.read-only viewer keeps `null` and the
  // columns fall back to the vocabulary (pinned to the code catalogue).
  const [catalogueActions, setCatalogueActions] = useState<string[] | null>(null);

  useEffect(() => {
    if (!canRead) return;
    v2.roles
      .list()
      .then(setRoles)
      .catch((err: unknown) => {
        setError(formatApiError(err, 'Failed to load roles'));
        setRoles([]);
      });
  }, [canRead, reloadKey]);

  useEffect(() => {
    if (!canManage) return;
    v2.permissions
      .list()
      .then((list) => {
        // An empty answer is not a real catalogue (roles hold permissions);
        // keep the vocabulary columns rather than show fewer.
        setCatalogueActions(
          Array.isArray(list) && list.length > 0 ? [...new Set(list.map((p) => p.Action))] : null,
        );
      })
      // The vocabulary fallback still shows every held grant.
      .catch(() => setCatalogueActions(null));
  }, [canManage, reloadKey]);

  const columns = useMemo(
    () =>
      permissionColumns(
        catalogueActions,
        (roles ?? []).flatMap((r) => r.Permissions.map((p) => p.Action)),
      ),
    [catalogueActions, roles],
  );

  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  if (!perms.hasPermission('Users', 'read')) return <PermissionDenied />;

  const refresh = (): void => setReloadKey((k) => k + 1);

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Roles &amp; Permissions</h1>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
            System roles are seeded and read-only. Admins can create, edit, and
            retire custom roles — roles are never deleted.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setModal({ kind: 'create' })}
            style={createBtnStyle}
            data-testid="roles-create-btn"
          >
            + Create role
          </button>
        )}
      </div>

      {/* phase-roles-and-users-fixes item 3 — the permission vocabulary,
          defined on the screen. The five columns previously appeared with no
          definition anywhere, on the screen that governs who may do what. */}
      <details data-testid="permission-legend" style={{ marginBottom: 16 }}>
        <summary
          style={{
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            padding: '8px 12px',
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        >
          What these permissions mean
        </summary>
        <dl
          style={{
            margin: '8px 0 0 0',
            padding: '12px 16px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--surface)',
            fontSize: 13,
          }}
        >
          {columns.map((a) => (
            <div key={a} style={{ marginBottom: 8 }}>
              <dt
                style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 12 }}
                data-testid={`permission-legend-term-${a}`}
              >
                {describeAction(a).label}
                {!describeAction(a).enforced && (
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                    {' '}
                    — not yet in force
                  </span>
                )}
              </dt>
              <dd style={{ margin: '2px 0 0 0', color: 'var(--text-muted)' }}>
                {describeAction(a).definition}
              </dd>
            </div>
          ))}
          <div style={{ marginTop: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            A dash (—) means the role does not have that permission for that
            resource.
            {columns.some((a) => !describeAction(a).enforced) &&
              ' Columns marked “not yet in force” are declared on roles but are not checked anywhere in the system today.'}
          </div>
        </dl>
      </details>

      {error && (
        <div role="alert" style={{ color: 'var(--red)' }}>
          {error}
        </div>
      )}
      {roles === null && !error && <Spinner caption="Loading roles…" />}
      {roles && roles.length === 0 && !error && (
        <EmptyState
          icon="🛡️"
          heading="No roles defined"
          subtext="Roles are seeded from code. If this list is empty the seed migration has not run."
        />
      )}

      {roles && roles.length > 0 && (
        <>
          {roles.some((r) => r.IsRetired) && (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: 'var(--text-muted)',
                marginBottom: 12,
              }}
            >
              <input
                type="checkbox"
                checked={showRetired}
                onChange={(e) => setShowRetired(e.target.checked)}
                data-testid="roles-show-retired-toggle"
              />
              Show retired
            </label>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {sortRoles(roles)
              .filter((r) => showRetired || !r.IsRetired)
              .map((r) => (
                <RoleCard
                  key={r.Id}
                  role={r}
                  columns={columns}
                  canManage={canManage}
                  onEdit={() => setModal({ kind: 'edit', role: r })}
                  onRetire={() => setModal({ kind: 'retire', role: r })}
                  onReactivated={refresh}
                />
              ))}
          </div>
        </>
      )}

      {modal?.kind === 'create' && (
        <RoleEditorModal
          mode="create"
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            refresh();
          }}
        />
      )}
      {modal?.kind === 'edit' && modal.role && (
        <RoleEditorModal
          mode="edit"
          role={modal.role}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            refresh();
          }}
        />
      )}
      {modal?.kind === 'retire' && modal.role && (
        <RetireRoleModal
          role={modal.role}
          onClose={() => setModal(null)}
          onRetired={() => {
            setModal(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

/** Active roles first (each group alphabetical); retired sort after. */
function sortRoles(roles: Role[]): Role[] {
  return [...roles].sort((a, b) => {
    if (Boolean(a.IsRetired) !== Boolean(b.IsRetired)) return a.IsRetired ? 1 : -1;
    return a.Name.localeCompare(b.Name);
  });
}

interface RoleCardProps {
  role: Role;
  /** T-07 (D-28) — the matrix columns (catalogue actions + every held action). */
  columns: readonly string[];
  canManage: boolean;
  onEdit: () => void;
  onRetire: () => void;
  onReactivated: () => void;
}

function RoleCard({ role, columns, canManage, onEdit, onRetire, onReactivated }: RoleCardProps): ReactElement {
  // Group permissions by resource for tabular display. Every held action is
  // kept: `columns` always includes it, so no grant is dropped (D-28).
  const byResource = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const p of role.Permissions) {
      const set = map.get(p.Resource) ?? new Set<string>();
      set.add(p.Action);
      map.set(p.Resource, set);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [role]);

  const isSystem = role.IsSystemRole === true;
  const isRetired = role.IsRetired === true;

  return (
    <article
      style={{
        border: '1px solid var(--border)',
        borderRadius: 10,
        background: 'var(--surface)',
        padding: 20,
        // phase-fix-role-retire-not-delete — retired roles are greyed.
        opacity: isRetired ? 0.6 : 1,
      }}
      data-testid={`role-card-${role.Name}`}
      data-retired={isRetired ? 'true' : 'false'}
    >
      <header
        style={{
          marginBottom: 12,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: 'var(--accent)' }}>
            {role.Name}
            {isSystem && (
              <span style={badgeStyle} data-testid={`role-system-badge-${role.Name}`}>
                System
              </span>
            )}
            {isRetired && (
              <span
                style={{ ...badgeStyle, color: 'var(--red, #B91C1C)' }}
                data-testid={`role-retired-badge-${role.Name}`}
              >
                Retired
              </span>
            )}
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {role.Description}
          </p>
        </div>
        {canManage && !isSystem && (
          <div style={{ display: 'flex', gap: 8 }}>
            {!isRetired && (
              <>
                <button
                  type="button"
                  onClick={onEdit}
                  style={buttonStyle}
                  data-testid={`role-edit-btn-${role.Name}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={onRetire}
                  style={dangerBtnStyle}
                  data-testid={`role-retire-btn-${role.Name}`}
                >
                  Retire
                </button>
              </>
            )}
            {isRetired && (
              <ReactivateButton role={role} onReactivated={onReactivated} />
            )}
          </div>
        )}
      </header>

      <ResponsiveTable
        data-testid={`role-permissions-table-${role.Name}`}
        style={{ width: '100%', borderCollapse: 'collapse' }}
      >
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ ...cellStyle, textAlign: 'left' }}>Resource</th>
            {columns.map((a) => (
              <th
                key={a}
                style={cellStyle}
                /* Hover definition; the legend above the table carries the
                   same text for anyone not using a mouse. */
                title={`${describeAction(a).label} — ${describeAction(a).definition}`}
                data-testid={`permission-header-${a}`}
              >
                {a}
                {!describeAction(a).enforced && (
                  /* Visual "not yet in force" marker. aria-hidden so the
                     column header's accessible name stays the action name
                     (a header should be named for its column); screen-reader
                     users get the same fact as real text in the legend. */
                  <span
                    aria-hidden="true"
                    data-testid={`permission-header-inert-${a}`}
                    title="Declared on roles but not yet enforced anywhere in the system."
                    style={{ marginLeft: 4, color: 'var(--text-muted)', fontWeight: 400 }}
                  >
                    *
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {byResource.length === 0 ? (
            <tr data-testid={`role-permissions-empty-row-${role.Name}`}>
              <td
                colSpan={columns.length + 1}
                style={{
                  padding: '24px 12px',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  No permissions assigned
                </div>
                <div>
                  This role has no permissions
                  {isSystem
                    ? ' in the seed migration. If this is unexpected, check the role\'s seed entry in migrations.'
                    : '. Edit the role to add permissions, or retire it if it is no longer needed.'}
                </div>
              </td>
            </tr>
          ) : (
            byResource.map(([resource, actions]) => (
              <tr key={resource} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>{resource}</td>
                {columns.map((a) => (
                  <td key={a} style={cellStyle}>
                    {actions.has(a) ? (
                      <span style={{ color: 'var(--green)' }} aria-label={`${resource}:${a}`}>
                        ✓
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', opacity: 0.4 }}>—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </ResponsiveTable>
    </article>
  );
}

// ── Modals ─────────────────────────────────────────────────────────────

interface RoleEditorModalProps {
  mode: 'create' | 'edit';
  role?: Role;
  onClose: () => void;
  onSaved: () => void;
}

function RoleEditorModal({ mode, role, onClose, onSaved }: RoleEditorModalProps): ReactElement {
  const [name, setName] = useState(role?.Name ?? '');
  const [description, setDescription] = useState(role?.Description ?? '');
  const initialSelected = useMemo(
    () =>
      new Set((role?.Permissions ?? []).map((p) => `${p.Resource}.${p.Action}`)),
    [role],
  );
  const [selected, setSelected] = useState<Set<string>>(initialSelected);
  const [catalogue, setCatalogue] = useState<
    Array<{ Resource: string; Action: string }> | null
  >(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    v2.permissions
      .list()
      .then(setCatalogue)
      .catch((err: unknown) => {
        setSubmitError(formatApiError(err, 'Failed to load permissions'));
        setCatalogue([]);
      });
  }, []);

  const toggle = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // T-07 (T07-D2) — permissions this role holds that the catalogue no longer
  // offers (GET /permissions leaves out retired ones). They grant nothing and
  // cannot be granted again, so saving removes them — and the dialog says so.
  const noLongerOffered = useMemo(() => {
    if (!catalogue || catalogue.length === 0) return [];
    const offered = new Set(catalogue.map((p) => `${p.Resource}.${p.Action}`));
    return [...initialSelected].filter((k) => !offered.has(k)).sort();
  }, [catalogue, initialSelected]);

  const submit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setSubmitError(null);
    const body: RoleWriteBody = {
      name: name.trim(),
      description: description.trim(),
      // T-07 (T07-D2) — only permissions that can still be granted; the
      // server refuses a retired one (400 PermissionRetired).
      permissions: Array.from(selected).filter((k) => !noLongerOffered.includes(k)).map((k) => {
        const [Resource, Action] = k.split('.');
        return { Resource: Resource ?? '', Action: Action ?? '' };
      }),
    };
    try {
      if (mode === 'create') {
        await v2.roles.create(body);
      } else if (role) {
        await v2.roles.update(role.Id, body);
      }
      onSaved();
    } catch (err) {
      // T-07 (D-28) — the server's message, never the raw JSON body.
      setSubmitError(formatApiError(err, 'Save failed'));
      setBusy(false);
    }
  };

  // Group catalogue by resource for the checkbox grid.
  const grouped = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of catalogue ?? []) {
      const list = m.get(p.Resource) ?? [];
      list.push(p.Action);
      m.set(p.Resource, list);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalogue]);

  return (
    <MovableDialog persistKey="roles.editor" testId="role-editor-modal" width={600}
      title={mode === 'create' ? 'Create role' : `Edit role: ${role?.Name ?? ''}`}
      ariaLabel={mode === 'create' ? 'Create role' : `Edit role: ${role?.Name ?? ''}`}
      onClose={onClose} escDisabled={busy}>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="role-name" style={{ display: 'block', fontWeight: 600 }}>
              Name
            </label>
            <input
              id="role-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={100}
              data-testid="role-name-input"
              style={{ width: '100%', padding: '6px 10px', marginTop: 4 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="role-desc" style={{ display: 'block', fontWeight: 600 }}>
              Description
            </label>
            <input
              id="role-desc"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              data-testid="role-desc-input"
              style={{ width: '100%', padding: '6px 10px', marginTop: 4 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Permissions</div>
            {catalogue === null && <Spinner caption="Loading permissions…" />}
            {catalogue && catalogue.length === 0 && (
              <p style={{ color: 'var(--text-muted)' }}>No permissions available.</p>
            )}
            {noLongerOffered.length > 0 && (
              <p data-testid="role-retired-permissions-note" style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
                {noLongerOffered.map((k) => k.replace('.', ':')).join(', ')}{' '}
                {noLongerOffered.length === 1 ? 'is' : 'are'} retired and can no longer be granted. Saving removes{' '}
                {noLongerOffered.length === 1 ? 'it' : 'them'} from this role; nothing in the system checks {noLongerOffered.length === 1 ? 'it' : 'them'}.
              </p>
            )}
            {grouped.length > 0 && (
              <div data-testid="role-permissions-grid">
                {grouped.map(([resource, actions]) => (
                  <fieldset
                    key={resource}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      padding: 8,
                      marginBottom: 8,
                    }}
                  >
                    <legend style={{ fontWeight: 600, padding: '0 8px' }}>{resource}</legend>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {actions.sort().map((action) => {
                        const key = `${resource}.${action}`;
                        return (
                          <label
                            key={action}
                            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                          >
                            <input
                              type="checkbox"
                              checked={selected.has(key)}
                              onChange={() => toggle(key)}
                              data-testid={`role-perm-${resource}-${action}`}
                            />
                            {action}
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>
            )}
          </div>
          {submitError && (
            <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>
              {submitError}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} style={buttonStyle}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              style={createBtnStyle}
              data-testid="role-save-btn"
            >
              {busy
                ? 'Saving…'
                : mode === 'create'
                  ? 'Create role'
                  : 'Save changes'}
            </button>
          </div>
        </form>
    </MovableDialog>
  );
}

// phase-fix-role-retire-not-delete — roles are never deleted, only
// retired (QMS obsolete-not-destroyed). The RetireRoleModal replaces the
// old DeleteRoleModal; ReactivateButton brings a retired role back.

interface RetireRoleModalProps {
  role: Role;
  onClose: () => void;
  onRetired: () => void;
}

function RetireRoleModal({ role, onClose, onRetired }: RetireRoleModalProps): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await v2.roles.retire(role.Id);
      onRetired();
    } catch (err) {
      setError(formatApiError(err, 'Retire failed'));
      setBusy(false);
    }
  };

  return (
    <MovableDialog persistKey="roles.retire" testId="role-retire-modal" width={480}
      title={`Retire role: ${role.Name}`} ariaLabel={`Retire role: ${role.Name}`} onClose={onClose} escDisabled={busy}>
        <p>
          This role will stop granting permissions and can no longer be
          assigned. Its definition and history are preserved. You can
          reactivate it later.
        </p>
        {error && (
          <div
            role="alert"
            data-testid="role-retire-error"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg, #FEE2E2)',
              border: '1px solid var(--red, #B91C1C)',
              borderRadius: 4,
              padding: '8px 12px',
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={buttonStyle}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void submit();
            }}
            disabled={busy}
            style={dangerBtnStyle}
            data-testid="role-retire-confirm-btn"
          >
            {busy ? 'Retiring…' : 'Retire role'}
          </button>
        </div>
    </MovableDialog>
  );
}

interface ReactivateButtonProps {
  role: Role;
  onReactivated: () => void;
}

function ReactivateButton({ role, onReactivated }: ReactivateButtonProps): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const submit = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await v2.roles.reactivate(role.Id);
      onReactivated();
    } catch (err) {
      setError(formatApiError(err, 'Reactivate failed'));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={busy}
        style={buttonStyle}
        data-testid={`role-reactivate-btn-${role.Name}`}
      >
        {busy ? 'Reactivating…' : 'Reactivate'}
      </button>
      {error && (
        <span role="alert" style={{ color: 'var(--red)', fontSize: 12 }}>
          {error}
        </span>
      )}
      {/* D-T07-04 — reactivating a role silently restores every permission it
          grants to every user still assigned to it, so it is confirmed first. */}
      {confirming && (
        <ConfirmModal
          title={`Reactivate role: ${role.Name}`}
          message={`Reactivate ${role.Name}? Every user still assigned to it gets its permissions back immediately, and it can be assigned again.`}
          confirmLabel="Reactivate role"
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            void submit();
          }}
        />
      )}
    </div>
  );
}
